import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ageFromBirthdate,
  type Role,
  TIER_ORDER,
  TIERS,
  type TierId,
  clampStreams,
} from '@gobingebox/shared';
import { Ban } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Check, Loader2, Minus, Plus, Search, X } from '@/components/icons';
import { adminApi, type AdminUser } from './api';
import { useAuth } from '@/features/auth/AuthContext';

function GrantCompForm({ user, onDone }: { user: AdminUser; onDone: () => void }) {
  const qc = useQueryClient();
  const [tier, setTier] = useState<TierId>('bingebox');
  const [streams, setStreams] = useState(1);
  const grant = useMutation({
    mutationFn: () => adminApi.grantComp(user.id, { tier, streams }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      onDone();
    },
  });

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface p-3">
      <select
        value={tier}
        onChange={(e) => setTier(e.target.value as TierId)}
        className="h-9 rounded-lg border border-border bg-surface-raised px-2 text-sm text-ink"
      >
        {TIER_ORDER.map((id) => (
          <option key={id} value={id}>
            {TIERS[id].name}
          </option>
        ))}
      </select>
      <div className="inline-flex items-center rounded-lg border border-border bg-surface-raised">
        <button type="button" className="grid h-9 w-8 place-items-center text-ink-muted hover:text-ink disabled:opacity-40" disabled={streams <= 1} onClick={() => setStreams((s) => clampStreams(s - 1))}>
          <Minus size={14} />
        </button>
        <span className="w-8 text-center text-sm tabular-nums text-ink">{streams}</span>
        <button type="button" className="grid h-9 w-8 place-items-center text-ink-muted hover:text-ink disabled:opacity-40" disabled={streams >= 8} onClick={() => setStreams((s) => clampStreams(s + 1))}>
          <Plus size={14} />
        </button>
      </div>
      <span className="text-xs text-ink-faint">streams</span>
      <Button size="sm" onClick={() => grant.mutate()} disabled={grant.isPending}>
        Grant free access
      </Button>
      <button type="button" onClick={onDone} className="text-sm text-ink-muted hover:text-ink">
        Cancel
      </button>
    </div>
  );
}

function UserRow({ user, canManageOwner, meId, meRole }: { user: AdminUser; canManageOwner: boolean; meId: string; meRole: Role }) {
  const qc = useQueryClient();
  const [granting, setGranting] = useState(false);
  const [banning, setBanning] = useState(false);
  const [reason, setReason] = useState('');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-users'] });

  const setRole = useMutation({
    mutationFn: (role: Role) => adminApi.setRole(user.id, role),
    onSuccess: invalidate,
  });
  const revoke = useMutation({
    mutationFn: () => adminApi.revokeComp(user.id),
    onSuccess: invalidate,
  });
  const ban = useMutation({
    mutationFn: () => adminApi.ban(user.id, reason.trim() || undefined),
    onSuccess: () => { invalidate(); setBanning(false); setReason(''); },
  });
  const unban = useMutation({ mutationFn: () => adminApi.unban(user.id), onSuccess: invalidate });

  const isStaff = user.role === 'owner' || user.role === 'admin';
  const comp = user.subscription?.source === 'comp' ? user.subscription : null;
  const canBan =
    user.id !== meId &&
    user.role !== 'owner' &&
    (meRole === 'owner' || (user.role !== 'admin' && user.role !== 'contributor'));

  return (
    <li className={cn('card p-4', user.banned && 'border-danger/40 bg-danger/5')}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand-gradient text-sm font-bold text-white">
          {(user.displayName || user.email).charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{user.displayName}</p>
          <p className="truncate text-xs text-ink-faint">
            {user.email}
            {user.birthdate && ` · age ${ageFromBirthdate(user.birthdate)}`}
          </p>
          {user.banned && (
            <p className="mt-0.5 truncate text-xs font-medium text-danger">
              Suspended{user.bannedReason ? ` · ${user.bannedReason}` : ''}
            </p>
          )}
        </div>

        {/* role */}
        <select
          value={user.role}
          disabled={setRole.isPending || (user.role === 'owner' && !canManageOwner)}
          onChange={(e) => setRole.mutate(e.target.value as Role)}
          className="h-9 rounded-lg border border-border bg-surface-raised px-2 text-sm text-ink disabled:opacity-50"
          aria-label={`Role for ${user.email}`}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
          {(canManageOwner || user.role === 'owner') && <option value="owner">Owner</option>}
        </select>

        {/* access */}
        <div className="flex items-center gap-2">
          {isStaff ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand/15 px-2.5 py-1 text-2xs font-semibold uppercase tracking-wide text-brand-soft">
              <Check size={12} /> Full (staff)
            </span>
          ) : comp ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-2.5 py-1 text-2xs font-semibold uppercase tracking-wide text-success">
              Comp · {TIERS[comp.tier].name} · {comp.streams}×
            </span>
          ) : user.subscription ? (
            <span className="rounded-full bg-surface-raised px-2.5 py-1 text-2xs font-semibold uppercase tracking-wide text-ink-muted">
              {TIERS[user.subscription.tier].name}
            </span>
          ) : (
            <span className="rounded-full bg-surface-raised px-2.5 py-1 text-2xs text-ink-faint">
              No access
            </span>
          )}

          {!isStaff &&
            (comp ? (
              <button
                type="button"
                onClick={() => revoke.mutate()}
                disabled={revoke.isPending}
                className="grid h-8 w-8 place-items-center rounded-full text-ink-muted hover:bg-surface-raised hover:text-danger"
                aria-label="Revoke comp access"
              >
                <X size={15} />
              </button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setGranting((v) => !v)}>
                Grant free
              </Button>
            ))}
        </div>

        {/* ban / suspend */}
        {user.banned ? (
          <Button size="sm" variant="outline" onClick={() => unban.mutate()} disabled={unban.isPending}>
            Unban
          </Button>
        ) : canBan ? (
          <button
            type="button"
            onClick={() => setBanning((v) => !v)}
            className="grid h-8 w-8 place-items-center rounded-full text-ink-faint hover:bg-surface-raised hover:text-danger"
            title="Suspend user"
            aria-label={`Suspend ${user.email}`}
          >
            <Ban size={15} />
          </button>
        ) : null}
      </div>

      {granting && !comp && !isStaff && (
        <GrantCompForm user={user} onDone={() => setGranting(false)} />
      )}
      {banning && !user.banned && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-danger/30 bg-danger/5 p-3">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional — shown to staff)"
            maxLength={280}
            className="h-9 min-w-[200px] flex-1 rounded-lg border border-border bg-surface-raised px-3 text-sm text-ink"
          />
          <button
            type="button"
            onClick={() => ban.mutate()}
            disabled={ban.isPending}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-danger px-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            <Ban size={14} /> Suspend account
          </button>
          <button type="button" onClick={() => setBanning(false)} className="text-sm text-ink-muted hover:text-ink">
            Cancel
          </button>
        </div>
      )}
    </li>
  );
}

export function Members() {
  const { user } = useAuth();
  const canManageOwner = user?.role === 'owner';
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setQ(term.trim()), 300);
    return () => clearTimeout(id);
  }, [term]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', q],
    queryFn: () => adminApi.users(q || undefined),
  });

  return (
    <div>
      <div className="relative mb-4 max-w-md">
        <Search size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search members by name or email…"
          className="pl-11"
        />
      </div>

      {isLoading ? (
        <p className="flex items-center gap-2 text-sm text-ink-faint">
          <Loader2 size={16} className="animate-spin" /> Loading members…
        </p>
      ) : (
        <ul className={cn('space-y-2.5')}>
          {(data?.users ?? []).map((u) => (
            <UserRow
              key={u.id}
              user={u}
              canManageOwner={canManageOwner}
              meId={user?.id ?? ''}
              meRole={user?.role ?? 'user'}
            />
          ))}
          {data && data.users.length === 0 && (
            <li className="text-sm text-ink-muted">No members found.</li>
          )}
        </ul>
      )}
    </div>
  );
}
